import {memo} from 'react';
import type {ReactNode} from 'react';
import type {StaticImageData} from 'next/image';
import Container from '@/components/atoms/Container';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './KeyFacts.module.scss';

// Outline glyphs (Lucide, ISC) drawn with `currentColor`, so the card's state colours them.
const ICONS = {
    star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
    cube: (
        <>
            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
            <path d="m3.3 7 8.7 5 8.7-5" />
            <path d="M12 22V12" />
        </>
    ),
    users: (
        <>
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
    ),
    award: (
        <>
            <circle cx="12" cy="8" r="6" />
            <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
        </>
    ),
    building: (
        <>
            <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
            <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
            <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
            <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
        </>
    ),
    trend: (
        <>
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
            <polyline points="16 7 22 7 22 13" />
        </>
    ),
    globe: (
        <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
            <path d="M2 12h20" />
        </>
    )
} satisfies Record<string, ReactNode>;

export type KeyFactIcon = keyof typeof ICONS;

export interface KeyFact {
    /** The headline figure, e.g. `10+`. */
    value: string;
    label: string;
    icon: KeyFactIcon;
}

export interface KeyFactsProps {
    /** Optional: omit for a facts-only strip. */
    heading?: string;
    facts: KeyFact[];
    /** Optional photo behind the panel, washed out so the cards stay the focus. Decorative (empty alt). */
    backgroundImage?: string | StaticImageData;
    /** `compact` sets the value at card-title size, for word values ("Full Lifecycle") rather than short figures. */
    variant?: 'default' | 'compact';
}

// Every card is white; hovering one turns it navy.
const FactCard = ({value, label, icon}: KeyFact) => (
    <li className={styles.card}>
        <span className={styles.iconBox} aria-hidden="true">
            <svg
                className={styles.icon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                focusable="false"
            >
                {ICONS[icon]}
            </svg>
        </span>
        <p className={`font-headers ${styles.value}`}>{value}</p>
        <p className={`font-regular-text ${styles.label}`}>{label}</p>
    </li>
);

const KeyFacts = ({heading, facts, backgroundImage, variant = 'default'}: KeyFactsProps) => (
    <Container>
        <div className={styles.panel}>
            {backgroundImage && (
                <OptimizedImage src={backgroundImage} alt="" fill sizes="(min-width: 1200px) 1200px, 100vw" className={styles.bgImage} />
            )}
            <div className={styles.content}>
                {heading && <h2 className={`font-headers ${styles.heading}`}>{heading}</h2>}
                <ul className={variant === 'compact' ? `${styles.grid} ${styles.compact}` : styles.grid}>
                    {facts.map((fact) => (
                        <FactCard key={fact.label} {...fact} />
                    ))}
                </ul>
            </div>
        </div>
    </Container>
);

export default memo(KeyFacts);
